/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import {
    IDLE_GRID_GUIDE_DURATION,
    MAX_GRID_GUIDE_PROGRAM_CELL_LENGTH,
    MILLISECONDS_IN_A_MINUTE,
    MILLISECONDS_IN_A_SECOND,
} from '../../../config';
import { AppUtils } from '../../../core/AppUtils';
import { DeviceFeatures } from '../../../core/device';
import { Channel } from '../../../core/entities/Channel';
import { GridGuideRow } from '../../../core/entities/GridGuideRow';
import { GuideCell } from '../../../core/entities/GuideCell';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { ChannelCell } from '../ChannelCell';
import { GuideHighlight } from '../GuideHighlight';
import { GuideProps } from '../GuideProps';
import { ProgramCell } from '../ProgramCell';
import { CellTypes } from '../ProgramCell/CellTypes';
import { Row } from '../Row';

import './PastGuide.scss';

const PastGuide: FunctionComponent<GuideProps> = ({
    limit: LIMIT,
    channels,
    guideRows,
    currentTimestamp,
    nextTimestamp,
    visibilityState,
    hideMetadata,
    recordingsStatusMap,
}): JSX.Element => {
    const createGuideWithNoInformation = () => {
        return new Array(LIMIT).fill(0).map((_, index) => {
            return createRow(index);
        });
    };

    const createGuideWithChannelInformation = (aChannels) => {
        if (!aChannels || aChannels.length === 0) return null;
        const channelRowNumber = Math.min(LIMIT, aChannels.length);

        return new Array(channelRowNumber).fill(0).map((_, index) => {
            return createRow(index, aChannels);
        });
    };

    const createGuideWithChannelAndGuideRows = (aChannels, aGuideRows) => {
        const isNotChannels = !aChannels || aChannels.length === 0;
        const isNotGuideRows = !aGuideRows || aGuideRows.length === 0;
        if (isNotChannels || isNotGuideRows) return null;

        return new Array(LIMIT).fill(0).map((_, index) => {
            return (
                aGuideRows[index] && (
                    <Row key={index}>
                        {createProgramCell(aGuideRows[index], CellTypes.TRAILING)}
                        {createGuideProgramCells(aGuideRows[index], aChannels[index]) || (
                            <ProgramCell
                                type={CellTypes.PAST_CONTINUOUS}
                                duration={MAX_GRID_GUIDE_PROGRAM_CELL_LENGTH}
                            />
                        )}
                        <div className="shift-right">
                            <ChannelCell
                                channel={aChannels[index]}
                                highlightId={AppUtils.createHighlightId(0, index)}
                            >
                                <GuideHighlight id={AppUtils.createHighlightId(0, index)} />
                            </ChannelCell>
                            {createProgramCell(aGuideRows[index], CellTypes.LEADING) || (
                                <ProgramCell type={CellTypes.LEADING} />
                            )}
                        </div>
                    </Row>
                )
            );
        });
    };

    const createProgramCell = (aGuideRow, cellType) => {
        const guideCells = aGuideRow?.guideCells;
        const isValidGuideCells = guideCells && guideCells?.length > 0;
        if (!isValidGuideCells) return null;

        if (guideCells.length === 1 && cellType === CellTypes.TRAILING) return null;

        const guideCell =
            cellType === CellTypes.TRAILING ? guideCells[0] : guideCells[guideCells.length - 1];
        const { title, isAdult } = guideCell;

        const isPastContinousCell =
            cellType === CellTypes.TRAILING &&
            new Date(guideCells[0]?.startTime).getTime() ===
                new Date(guideCells[1]?.startTime).getTime();

        if (isPastContinousCell) return null;

        return (
            <ProgramCell
                type={cellType}
                title={
                    isAdult && hideMetadata
                        ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
                        : title
                }
            />
        );
    };

    let rowCount: number = 0;

    const createGuideProgramCells = (aGuideRow: GridGuideRow, aChannel: Channel) => {
        const guideCells = aGuideRow?.guideCells;
        const isValidGuideCells = guideCells && guideCells?.length > 0;
        if (!isValidGuideCells) return [];

        let currLen = 0;
        let counter = 0;

        const mGuideCells: GuideCell[] = guideCells.length === 1 ? guideCells : guideCells.slice(1);
        const programCells = mGuideCells.map((guideCell) => {
            const {
                title,
                startTime,
                duration,
                isNew,
                isAdult,
                movieYear,
                isStartover,
                isCatchup,
                offerId,
            } = guideCell;
            if (currLen >= IDLE_GRID_GUIDE_DURATION) return [];

            const isSocuEnabled: boolean = DeviceFeatures.isSocuEnabled();

            const mStartTime = new Date(startTime);

            const actualStartTime = mStartTime < currentTimestamp ? currentTimestamp : mStartTime;
            const endTime: any = AppUtils.addSecondsToDateTime(mStartTime, duration);

            const key = `stationId-${counter}`;
            counter += 1;

            let cellType = CellTypes.DEFAULT;
            let isLeft = false;
            let isRight = false;

            const expectedCellLength =
                (endTime.getTime() - actualStartTime.getTime()) / MILLISECONDS_IN_A_MINUTE;
            const lengthRemaining = IDLE_GRID_GUIDE_DURATION - currLen;
            let actualCellLength =
                expectedCellLength < lengthRemaining ? expectedCellLength : lengthRemaining;
            currLen += expectedCellLength;

            if (mStartTime < currentTimestamp) {
                isLeft = true;
                cellType = CellTypes.PAST_CONTINUOUS;
                actualCellLength += 6.25;
            }

            if (endTime > nextTimestamp) {
                cellType = CellTypes.FUTURE_CONTINUOUS;
                isRight = true;
            }

            const cuAvailabilityDuration =
                (aChannel.policyInfo?.find((policy) => policy?.socuRules)?.socuRules
                    ?.cuAvailabilityDurationSeconds || 0) * MILLISECONDS_IN_A_SECOND;

            const mIsAdult = isAdult && hideMetadata;
            const recordingStatus = recordingsStatusMap.get(`${aChannel.stationId}${offerId}`);

            return (
                <ProgramCell
                    key={key}
                    title={mIsAdult ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN) : title}
                    isNew={mIsAdult ? false : isNew}
                    isLeft={isLeft}
                    isRight={isRight}
                    type={cellType}
                    duration={actualCellLength}
                    highlightId={AppUtils.createHighlightId(counter, rowCount)}
                    isSubscribed={aChannel.isSubscribed}
                    isPastGuideMode={true}
                    movieYear={!mIsAdult ? movieYear : null}
                    startTime={mStartTime}
                    isCatchup={isSocuEnabled && isCatchup}
                    endTime={endTime}
                    cuAvailabilityDuration={cuAvailabilityDuration}
                    isStartover={isSocuEnabled && isStartover}
                    // TODO: refactor: move visibility state enum out of store
                    isRefreshed={visibilityState === 'ON_RESUME'}
                    channel={aChannel}
                    programCell={guideCell}
                    recordingStatus={recordingStatus}
                />
            );
        });
        rowCount += 1;
        return programCells;
    };

    const createGuide = (aChannels, aGuideRows) => {
        if (aGuideRows && aGuideRows.length > 0)
            return createGuideWithChannelAndGuideRows(aChannels, aGuideRows);
        if (aChannels && aChannels.length > 0) return createGuideWithChannelInformation(aChannels);
        return createGuideWithNoInformation();
    };

    const createRow = (index, aChannels?) => {
        const highlightId = AppUtils.createHighlightId(0, index);

        return (
            <Row key={index}>
                <ProgramCell type={CellTypes.TRAILING} />
                <ProgramCell
                    type={CellTypes.PAST_CONTINUOUS}
                    duration={MAX_GRID_GUIDE_PROGRAM_CELL_LENGTH}
                />
                <div className="shift-right">
                    {!aChannels ? (
                        <ChannelCell highlightId={highlightId}>
                            <GuideHighlight id={highlightId} />
                        </ChannelCell>
                    ) : (
                        <ChannelCell channel={aChannels[index]} highlightId={highlightId}>
                            <GuideHighlight id={highlightId} />
                        </ChannelCell>
                    )}
                    <ProgramCell type={CellTypes.LEADING} />
                </div>
            </Row>
        );
    };

    return <React.Fragment>{createGuide(channels, guideRows)}</React.Fragment>;
};

export default PastGuide;
